import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getGetVolumesQueryKey } from "~/api/folders/folders";
import {
  getGetProjectQueryKey,
  useGetProject,
  useUpdateProject,
} from "~/api/projects/projects";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { useProjectParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";

interface RenameVersionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const RenameVersionDialog: FC<RenameVersionDialogProps> = ({
  open,
  setOpen,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { data: project } = useGetProject(organizationId, caseId);
  const [versionName, setVersionName] = useState("");
  const { mutate: updateProject } = useUpdateProject();
  const [isPending, setIsPending] = useState(false);
  const queryClient = useQueryClient();
  const intl = useIntl();

  useEffect(() => {
    if (project) {
      setVersionName(project.name);
    }
  }, [project]);

  const handleRename = () => {
    trackEvent({
      eventName: "app/version_rename_modal/rename.button/submit",
    });

    setIsPending(true);

    updateProject(
      {
        organizationId,
        projectId: caseId,
        data: {
          name: versionName,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetProjectQueryKey(organizationId, caseId),
          });
          queryClient.invalidateQueries({
            queryKey: getGetVolumesQueryKey(organizationId, caseId),
          });
          // If not master version, invalidate parent case
          if (project?.parentId) {
            queryClient.invalidateQueries({
              queryKey: getGetVolumesQueryKey(organizationId, project.parentId),
            });
          }
          setOpen(false);
          setIsPending(false);
        },
        onError: () => {
          setIsPending(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="renameVersion"
              defaultMessage="Rename Version"
            />
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="name">
            <FormattedMessage
              id="versionNameLabel"
              defaultMessage="Version name"
            />
          </Label>
          <Input
            id="name"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            placeholder={intl.formatMessage({
              id: "versionNamePlaceholder",
              defaultMessage: "Enter version name",
            })}
            disabled={isPending}
            className="shadow-none"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              trackEvent({
                eventName: "app/version_rename_modal/cancel.button/click",
              });
              setOpen(false);
            }}
            disabled={isPending}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            disabled={!versionName.trim() || isPending}
            onClick={handleRename}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <FormattedMessage id="renaming" defaultMessage="Renaming..." />
              </>
            ) : (
              <FormattedMessage id="rename" defaultMessage="Rename" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
